import { useHistory } from "react-router-dom"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
import Box from "@material-ui/core/Box"
import Paper from "@material-ui/core/Paper"
import Card from "@material-ui/core/Card"
import CardContent from "@material-ui/core/CardContent"
import Grid from "@material-ui/core/Grid"
import makeStyles from "@material-ui/core/styles/makeStyles"
import AppLanguageSwitch from "../components/LanguageSwitch"
import { useI18n } from "../hooks"
import { useUrlWithContext } from "../hooks/use-url-with-context"
import { useAuthUser } from "../users/hooks/use-auth-user"
import { paths } from "../paths"
import { useLazySendWelcomeEmailQuery } from "../users/api"
import StarbrixLogo from "../assets/icons/StarbrixLogo"
import { User, Users } from "react-feather"

const ChooseIntroVideoType = () => {
  const history = useHistory()
  const classes = useStyles()
  const t = useTranslations(defaultTranslations)
  const { createPathWithGivenContext } = useUrlWithContext()
  const { user } = useAuthUser()
  const [sendWelcomeEmail] = useLazySendWelcomeEmailQuery()

  const handleClickPersonal = () => {
    if (user) {
      const welcomePath = createPathWithGivenContext({
        path: paths.userWelcome(),
        mainContextId: user.id,
      })
      sendWelcomeEmail({ type: "personalUse" })
      history.push(welcomePath)
    }
  }

  const handleClickOrganisational = () => {
    if (user) {
      const welcomePath = createPathWithGivenContext({
        path: paths.newOrganisation(),
        mainContextId: user.id,
      })
      sendWelcomeEmail({ type: "organisationalUse" })
      history.push(welcomePath)
    }
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.container} variant="outlined" elevation={0}>
        <div className={classes.logoWrapper}>
          <StarbrixLogo style={{ height: 48 }} />
        </div>
        <AppLanguageSwitch />
        <div>
          <Typography variant="h5" align="center" style={{ margin: 24 }} gutterBottom>
            {t.title}
          </Typography>
          <Grid container spacing={3} className={classes.cards}>
            <Grid container item xs={12} sm={6} justifyContent="center">
              <Card className={`${classes.organisationalCard}`} elevation={4}>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    <Users size={20} style={{ verticalAlign: "middle", marginRight: 16, marginBottom: 4 }} />
                    {t.organisationalTitle}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ maxWidth: 320 }}>
                    {t.organisationalDescription}
                  </Typography>
                </CardContent>
                <div className="CardActions">
                  <Typography variant="subtitle2" color="primary" style={{ marginBottom: 8 }}>
                    {t.organisationalPrice}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={() => handleClickOrganisational()}
                  >
                    {t.organisationalButton}
                  </Button>
                </div>
              </Card>
            </Grid>
            <Grid container item xs={12} sm={6} justifyContent="center">
              <Card variant="outlined" elevation={0}>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    <User size={20} style={{ verticalAlign: "middle", marginRight: 16, marginBottom: 4 }} />
                    {t.personalTitle}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ maxWidth: 320 }}>
                    {t.personalDescription}
                  </Typography>
                </CardContent>
                <div className="CardActions">
                  <Typography variant="subtitle2" color="primary" style={{ marginBottom: 8 }}>
                    {t.personalPrice}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={() => handleClickPersonal()}
                  >
                    {t.personalButton}
                  </Button>
                </div>
              </Card>
            </Grid>
          </Grid>
        </div>
      </Paper>
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  container: {
    padding: theme.spacing(4),
    maxWidth: 950,
  },
  logoWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 32,
  },
  cards: {
    marginTop: 24,
    "& .MuiCard-root": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      borderRadius: theme.spacing(0.5),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    "& .CardActions": {
      padding: theme.spacing(2, 2, 0, 2),
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  organisationalCard: {
    transition: "box-shadow 0.3s",
    "&:hover": {
      boxShadow: "0 16px 56px 0 rgba(25, 118, 210, 0.22)",
    },
  },
}))

const defaultTranslations = {
  title: "Welcome! How do you want to use Starbrix?",
  personalTitle: "Just me",
  personalDescription: "If you just want to manage your projects and tasks on your own.",
  personalPrice: "Completely free.",
  personalButton: "Choose Just me",
  organisationalTitle: "With my team",
  organisationalDescription:
    "If you're part of a team, company, or institution and want to collaborate on projects and tasks with colleagues, customers, suppliers, or other partners.",
  organisationalPrice: "Free for up to three users.",
  organisationalButton: "Choose With my team",
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t = {} } = useI18n("choose-intro-video-type-page")
  return {
    title: t.title || defaults.title,
    personalTitle: t.personalTitle || defaults.personalTitle,
    personalDescription: t.personalDescription || defaults.personalDescription,
    personalPrice: t.personalPrice || defaults.personalPrice,
    personalButton: t.personalButton || defaults.personalButton,
    organisationalTitle: t.organisationalTitle || defaults.organisationalTitle,
    organisationalDescription: t.organisationalDescription || defaults.organisationalDescription,
    organisationalButton: t.organisationalButton || defaults.organisationalButton,
    organisationalPrice: t.organisationalPrice || defaults.organisationalPrice,
  }
}

type Translations = typeof defaultTranslations

export default ChooseIntroVideoType
